import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/DeleteForever';
import EditIcon from '@material-ui/icons/Edit';

import { DeleteModal } from 'components';
import { Fragment } from 'react';

const useStyles = makeStyles(theme => ({
  root: { borderRadius: 0 },
  header: {
    paddingBottom: 0,
    '& .MuiCardHeader-content': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  content: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0
    }
  },
  description: {
    padding: theme.spacing(2, 3, 1, 3)
  },
  editButton: {
    marginLeft: theme.spacing(2)
  },
  deleteButton: {
    marginLeft: theme.spacing(1)
  },
  details: {
    padding: theme.spacing(1, 3)
  }
}));

const RepositoryCard = props => {
  const { repository, className, onEdit, onDelete, ...rest } = props;
  const classes = useStyles();
  const [openDelete, setOpenDelete] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [values, setValues] = useState(repository);

  useEffect(() => {
    setValues(repository);
    setEditMode(false);
  }, [repository])

  const handleChange = ({ target }) => {
    setValues(state => ({ ...state, [target.name]: target.value }))
  }

  const close = () => setEditMode(false)

  const handleEdit = () => {
    onEdit({ ...values, repoID: values.id }, close);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false)
  }

  const handleDeleteModal = () => {
    onDelete(repository.id, handleCloseDelete)
  }

  return (
    <Card
      {...rest}
      className={clsx(classes.root, className)}
    >
      <DeleteModal
        content={<div>Your are deleting <strong>{repository.name}</strong> repository. All datasets will be permanently deleted from your account and from accounts that the repository is shared with.</div>}
        onClose={handleCloseDelete}
        onDelete={handleDeleteModal}
        open={openDelete}
        title={repository.name}
      />
      <CardHeader
        className={classes.header}
        disableTypography
        subheader={<Fragment>
          {editMode ?
            <TextField
              autoFocus
              label="Name"
              margin="dense"
              name="name"
              onChange={handleChange}
              value={values.name || ''}
              variant="outlined"
            /> :
            <Typography
              color="textPrimary"
              display="inline"
              variant="h6"
            >
              {repository.name}
            </Typography>}
          <Typography
            display="inline"
            variant="body2"
          >
            &nbsp;| Created: {moment.utc(repository.created, 'MMM DD, YYYY h:mm:ss a').fromNow()}
          </Typography>
        </Fragment>
        }
        title={repository.title}
      />
      < CardContent className={classes.content} >
        <div className={classes.description}>
          {editMode ?
            <TextField
              fullWidth
              label="Description"
              margin="dense"
              multiline
              name="description"
              onChange={handleChange}
              value={values.description || ''}
              variant="outlined"
            /> :
            <Typography
              colo="textSecondary"
              variant="subtitle2"
            >
              {repository.description}
            </Typography>}
        </div>
        <Divider />
        <div className={classes.details}>
          <Grid
            alignItems="center"
            container
            justify="space-between"
            spacing={3}
          >
            <Grid item>
              <Typography variant="h5">{repository.datasets.length}</Typography>
              <Typography variant="body2">Datasets</Typography>
            </Grid>
            {editMode ?
              <Grid item>
                <Button
                  className={classes.editButton}
                  color="primary"
                  onClick={close}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  className={classes.editButton}
                  color="primary"
                  endIcon={<EditIcon />}
                  onClick={handleEdit}
                  variant="contained"
                >
                  Apply
                </Button>
              </Grid> :
              <Grid item>
                <Tooltip title="Edit Repository Info">
                  <IconButton
                    className={classes.likeButton}
                    onClick={() => setEditMode(true)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Repository">
                  <IconButton
                    className={classes.deleteButton}
                    onClick={() => setOpenDelete(true)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Grid>}
          </Grid>
        </div>
      </CardContent >
    </Card >
  );
};

RepositoryCard.propTypes = {
  className: PropTypes.string,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  repository: PropTypes.object.isRequired
};

RepositoryCard.defaultProps = {
  repository: {
    students: [],
    repositories: []
  }
};

export default RepositoryCard;
